import styled, { keyframes } from 'styled-components';
import { Card, StyledButton } from '../main/CommonStyles';

const fadeIn = keyframes`
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
`;

export const FormWrapper = styled(Card)`
    max-width: 800px;
    margin: 2rem auto;
    animation: ${fadeIn} 0.5s ease-out;
    padding: 2rem;
`;

export const StepIndicator = styled.div`
    display: flex;
    justify-content: space-around;
    margin-bottom: 2rem;
    counter-reset: step;

    .step {
        display: flex;
        flex-direction: column;
        align-items: center;
        position: relative;
        flex-grow: 1;

        .step-icon {
            width: 50px;
            height: 50px;
            border-radius: 50%;
            background: ${({ theme }) => theme.colors.neutralLight};
            border: 3px solid ${({ theme }) => theme.colors.borderColor};
            color: ${({ theme }) => theme.colors.textDisabled};
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.3s ease;
            z-index: 1;
            font-weight: 700;
        }

        .step-label {
            margin-top: 0.5rem;
            font-weight: 700;
            font-size: 0.8rem;
            color: ${({ theme }) => theme.colors.textDisabled};
            transition: color 0.3s ease;
            text-align: center;
        }

        &.active .step-icon {
            border-color: ${({ theme }) => theme.colors.primary};
            background-color: ${({ theme }) => theme.colors.primaryLightest};
            color: ${({ theme }) => theme.colors.primaryDark};
        }
        &.active .step-label {
            color: ${({ theme }) => theme.colors.primaryDark};
        }

        &.completed .step-icon {
            border-color: ${({ theme }) => theme.colors.secondary};
            background-color: ${({ theme }) => theme.colors.secondary};
            color: white;
        }
        &.completed .step-label {
            color: ${({ theme }) => theme.colors.secondaryDark};
        }

        &:not(:last-child)::after {
            content: '';
            position: absolute;
            top: 25px;
            left: 50%;
            width: 100%;
            height: 4px;
            background-color: ${({ theme }) => theme.colors.borderColor};
            transform: translateY(-50%);
            z-index: 0;
        }
    }
`;

export const FormStep = styled.div`
    animation: ${fadeIn} 0.4s ease-in-out;
`;

export const StepHeader = styled.div`
    text-align: center;
    margin-bottom: 2.5rem;
    h3 {
        font-size: 1.75rem;
        color: ${({ theme }) => theme.colors.textDark};
        margin-bottom: 0.5rem;
    }
    p {
        color: ${({ theme }) => theme.colors.textMedium};
        font-size: 1rem;
        max-width: 500px;
        margin: 0.5rem auto 0;
    }
`;

export const FormField = styled.div`
    margin-bottom: 1.5rem;
`;

export const FormLabel = styled.label`
    display: block;
    font-size: 1.1rem;
    font-weight: 700;
    color: ${({ theme }) => theme.colors.primaryDarker};
    margin-bottom: 0.75rem;
`;

const inputStyles = `
  width: 100%;
  padding: 1rem;
  font-size: 1.1rem;
  border: 2px solid ${({ theme }) => theme.colors.borderColor};
  border-radius: ${({ theme }) => theme.borderRadius};
  font-family: ${({ theme }) => theme.fonts.main};
  transition: all 0.2s ease;
  
  &:focus {
      border-color: ${({ theme }) => theme.colors.primary};
      box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primaryLightest};
      outline: none;
  }
`;

export const FormTextInput = styled.input`
    ${inputStyles}
`;

export const FormTextarea = styled.textarea`
    ${inputStyles}
    min-height: 150px;
    resize: vertical;
`;

export const RecipientCategory = styled.div`
    margin-bottom: 1.5rem;
    border: 2px solid ${({ theme }) => theme.colors.borderColor};
    border-radius: ${({ theme }) => theme.borderRadius};
    overflow: hidden;
    transition: all 0.3s ease;

    .category-header {
        background: ${({ theme }) => theme.colors.neutralLight};
        padding: 1rem 1.5rem;
        cursor: pointer;
        display: flex;
        justify-content: space-between;
        align-items: center;
        font-size: 1.25rem;
        font-weight: 700;
    }

    .person-list {
        padding: 1rem;
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
        background-color: ${({ theme }) => theme.colors.background};
    }
`;

export const PersonOption = styled.div`
  padding: 0.75rem 1rem;
  border: 2px solid ${({ theme, $selected }) => $selected ? theme.colors.primary : theme.colors.borderColor};
  border-radius: ${({ theme }) => theme.borderRadius};
  background: ${({ theme, $selected }) => $selected ? theme.colors.primaryLightest : theme.colors.white};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 1rem;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primaryLight};
    transform: translateX(5px);
  }

  .info {
    flex-grow: 1;
  }
  .name {
    font-weight: 700;
  }
  .title {
    font-size: 0.85rem;
    color: ${({ theme }) => theme.colors.textMedium};
  }
`;

export const OptionsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 1rem;
`;

export const Tag = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1.25rem;
    border: 2px solid ${({ theme, $selected }) => $selected ? theme.colors.primary : theme.colors.borderColor};
    background: ${({ theme, $selected }) => $selected ? theme.colors.primaryLightest : theme.colors.white};
    color: ${({ theme, $selected }) => $selected ? theme.colors.primaryDarker : theme.colors.textDark};
    border-radius: 50px;
    font-weight: 700;
    font-size: 1rem;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        border-color: ${({ theme }) => theme.colors.primaryLight};
    }
`;

export const TagInputWrapper = styled.div`
    .file-list {
        margin-top: 1rem;
        font-size: 0.9rem;
        color: ${({ theme }) => theme.colors.textMedium};
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
    }
`;

export const FormNavigation = styled.div`
    display: flex;
    justify-content: space-between;
    margin-top: 2.5rem;
`;

export const FormNavButton = styled(StyledButton)``;

export const FinalWarning = styled.div`
    background-color: ${({ theme }) => theme.colors.dangerBg};
    border: 2px solid ${({ theme }) => theme.colors.danger};
    padding: 1.5rem;
    border-radius: ${({ theme }) => theme.borderRadius};
    color: ${({ theme }) => theme.colors.danger};
    font-weight: 700;
    text-align: center;
    
    p { margin-bottom: 0.5rem; }
`;